import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaCheckCircle, FaClock, FaSync } from 'react-icons/fa';
import { catalogNodeService } from '../../../../services/apiService';

function formatDue(dueBy) {
  if (!dueBy) return '—';
  const d = new Date(dueBy);
  const hrs = Math.max(0, Math.round((d - Date.now()) / 3600000));
  if (hrs < 1) return 'Due soon';
  if (hrs < 24) return `${hrs}h left`;
  return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

export default function CatalogGapsPanel() {
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolvingId, setResolvingId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await catalogNodeService.listGaps();
      setGaps(res?.data || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load catalog gaps');
      setGaps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleResolve = async (gapId) => {
    try {
      setResolvingId(gapId);
      await catalogNodeService.resolveGap(gapId);
      toast.success('Gap resolved — catalog node created and pending listings activated');
      await load();
    } catch (err) {
      toast.error(err.message || 'Could not resolve gap');
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Catalog gap queue</h2>
          <p className="text-sm text-slate-500 mt-1">
            Listings waiting for a new 7-layer catalog node (24h SLA). Resolve to create the node and
            activate pending listings.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50"
        >
          <FaSync className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading gaps…</p>
      ) : gaps.length === 0 ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-6 py-10 text-center">
          <FaCheckCircle className="mx-auto text-3xl text-emerald-600 mb-2" />
          <p className="font-semibold text-emerald-900">No open catalog gaps</p>
          <p className="text-sm text-emerald-700 mt-1">All requested catalog nodes exist.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Part</th>
                <th className="px-4 py-3">Vehicle path</th>
                <th className="px-4 py-3">Profile</th>
                <th className="px-4 py-3">Due</th>
                <th className="px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {gaps.map((gap) => (
                <tr key={gap._id} className="hover:bg-slate-50/80">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{gap.partCategory}</div>
                    <div className="text-[10px] font-mono text-slate-400 truncate max-w-[200px]">
                      {gap.catalogNodeId}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {[gap.make, gap.model, gap.generation, gap.variant !== 'ALL' ? gap.variant : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-bold bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded">
                      {gap.profile}
                    </span>
                    <div className="text-xs text-slate-500 mt-0.5">{gap.subSystem}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-amber-700">
                      <FaClock className="text-xs" />
                      {formatDue(gap.dueBy)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      disabled={resolvingId === gap._id}
                      onClick={() => handleResolve(gap._id)}
                      className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-50"
                    >
                      {resolvingId === gap._id ? 'Resolving…' : 'Resolve'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
